import React, { useState } from 'react';
import { observer } from 'mobx-react';
import mainTheme from 'styles/themes/wikifactory';
import { Link, goBack } from 'core/routing';
import { useClientStore, useRouterStore } from 'apps/hooks';
import {
  MenuItem,
  Divisor,
  Dialog,
  NavTab,
  Avatar,
  Menu,
  Icon,
  Logo,
  Box,
} from 'components';
import { MobileFrameHeaderHeight } from 'styles/constants';
import { FrameMobileStyled } from './frameMobile.styled';
import Inbox from '../../Inbox';
import * as Icons from '../Frame/icons';
import WorkspaceSwitcherMenuItems from '../Frame/WorkspaceSwitcherMenuItems';

const communityRouteName = 'home.contribute.projects';

const navConfiguration = [
  {
    name: 'workspace.spaceFiles',
    fillParamWithActiveSpace: 'spacePath',
    tooltip: 'Files',
    iconName: 'cube',
    routes: ['workspace.spaceFiles'],
  },
  {
    name: 'workspace.spaceProjects',
    fillParamWithActiveSpace: 'spacePath',
    tooltip: 'Projects',
    iconName: 'folder',
    routes: ['workspace.spaceProjects'],
  },
  {
    name: 'workspace.rfqs',
    fillParamWithActiveSpace: 'spacePath',
    tooltip: 'RFQs',
    iconName: 'factory',
    routes: [
      'workspace.rfqs',
      'rfq',
      'workspace.intermediator.rfqs',
      'intermediator.rfq',
      'workspace.manufacturer.rfqs',
      'manufacturer.rfq',
    ],
  },
  {
    name: 'workspace.orders',
    fillParamWithActiveSpace: 'spacePath',
    tooltip: 'Orders',
    iconName: 'consumer',
    routes: [
      'workspace.orders',
      'order',
      'workspace.intermediator.orders',
      'intermediator.order',
      'workspace.manufacturer.orders',
      'manufacturer.order',
    ],
  },
];

type FrameMobileProps = {
  shouldHideLeftMenusOnMobile?: boolean;
};

const FrameMobile = observer(
  ({ shouldHideLeftMenusOnMobile }: FrameMobileProps) => {
    const [avatarEl, setAvatarEl] = useState<null | HTMLElement>(null);
    const [menuEl, setMenuEl] = useState<null | HTMLElement>(null);
    const [showInbox, setShowInbox] = useState(false);
    const routerStore = useRouterStore();
    const clientStore = useClientStore();

    const { route } = routerStore;

    let username = null;
    let content = null;

    if (clientStore?.signedInProfile) {
      ({ username, content } = clientStore.signedInProfile);
    }

    const { activeSpace } = clientStore;

    const collapsedAvatarUrl = activeSpace?.content?.avatar?.url;
    let collapsedAvatarName = activeSpace?.content?.slug;
    if (activeSpace?.spaceType === 'INITIATIVE') {
      collapsedAvatarName = activeSpace.content.title;
    }

    const handleCommunityBtnClick = () => {
      if (clientStore.atCommunityRoute) {
        goBack();
      } else {
        routerStore.navigate(communityRouteName);
      }
    };

    const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
      if (avatarEl) {
        setAvatarEl(null);
      } else {
        setAvatarEl(event.currentTarget);
      }
    };

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
      if (menuEl) {
        setMenuEl(null);
      } else {
        setMenuEl(event.currentTarget);
      }
    };

    const closeProfileMenu = () => {
      setAvatarEl(null);
    };

    const closeMenu = () => {
      setMenuEl(null);
    };

    const handleSignOut = () => {
      clientStore.SignOutUser();
    };

    const handleInboxClick = () => {
      setShowInbox(true);
    };

    const handleCloseInbox = () => {
      setShowInbox(false);
    };

    const navItems = navConfiguration.map(conf => {
      const isActive = conf.routes.includes(route.name);
      let params = {};
      if (conf.fillParamWithActiveSpace)
        params = {
          [conf.fillParamWithActiveSpace]: clientStore.activeSpace?.path,
        };

      return (
        <Link key={conf.name} params={params} name={conf.name}>
          <MenuItem selected={isActive}>
            <Icon name={conf.iconName} />
            <Box ml={2}>{conf.tooltip}</Box>
          </MenuItem>
        </Link>
      );
    });

    return (
      <>
        <FrameMobileStyled>
          <Link name='home'>
            <Logo size='20px' negativeVersion />
          </Link>
          {!shouldHideLeftMenusOnMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <NavTab
                active={clientStore.atCommunityRoute}
                data-for='tabsTooltip'
                data-tip='Community'
                data-place='right'
                onClick={handleCommunityBtnClick}
              >
                <Icon name='website' />
              </NavTab>
              <NavTab
                activeIconColor={mainTheme.color.wfBlack}
                onClick={handleMenuClick}
                active={Boolean(menuEl)}
              >
                {menuEl ? <Icon name='close' /> : <Icon name='menu' />}
              </NavTab>
              <NavTab
                activeIconColor={mainTheme.color.wfBlack}
                onClick={handleAvatarClick}
                active={Boolean(avatarEl)}
              >
                {avatarEl ? (
                  <Icon name='close' />
                ) : (
                  <Avatar src={collapsedAvatarUrl} name={collapsedAvatarName} />
                )}
              </NavTab>
            </Box>
          )}
        </FrameMobileStyled>
        <Dialog
          onClose={handleCloseInbox}
          open={showInbox}
          fullWidth
          // fullHeight
          sx={{
            overflow: 'hidden',
            left: 0,
            top: MobileFrameHeaderHeight,
            height: `calc(100% - ${MobileFrameHeaderHeight}px)`,
          }}
        >
          <Box sx={{ height: '100%' }}>
            <Icon
              onClick={handleCloseInbox}
              style={{
                right: mainTheme.space[3],
                top: mainTheme.space[3],
                position: 'absolute',
              }}
              name='close'
            />
            <Inbox onClose={handleCloseInbox} />
          </Box>
        </Dialog>
        {!shouldHideLeftMenusOnMobile && (
          <Menu
            open={Boolean(menuEl)}
            anchorEl={menuEl}
            onClose={closeMenu}
            onClick={closeMenu}
          >
            {navItems}
            <MenuItem onClick={handleInboxClick}>
              <Icon name='comment' />
              <Box ml={2}>Messages</Box>
            </MenuItem>
          </Menu>
        )}
        <Menu
          open={Boolean(avatarEl)}
          anchorEl={avatarEl}
          onClose={closeProfileMenu}
          onClick={closeProfileMenu}
        >
          <WorkspaceSwitcherMenuItems />
          <Divisor />
          <Link name='home.newInitiative'>
            <MenuItem>
              <Box mr={2}>
                <Icons.NewTeam />
              </Box>
              New Workspace
            </MenuItem>
          </Link>
          <Link name='profile' params={{ profile: username }} content={content}>
            <MenuItem>
              <Box mr={2}>
                <Icons.Profile />
              </Box>
              Community Profile
            </MenuItem>
          </Link>
          {/* <Link name='home.pricing'>
          <MenuItem>
            <Box mr={2}>
              <Icons.UpgradeAccount />
            </Box>
            Upgrade Account
          </MenuItem>
        </Link> */}
          <Link
            name='workspace.spaceSettings.account'
            params={{ spacePath: username }}
            content={content}
          >
            <MenuItem>
              <Box mr={2}>
                <Icons.Settings />
              </Box>
              Settings
            </MenuItem>
          </Link>
          <MenuItem onClick={handleSignOut}>
            <Box mr={2}>
              <Icons.SignOut />
            </Box>
            Sign Out
          </MenuItem>
        </Menu>
      </>
    );
  },
);

export default FrameMobile;
